import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@blueprintjs/core';

// Bars with more fields than this collapse behind a "Filters" toggle on phones.
const COLLAPSE_AFTER = 2;

// A cell counts as an applied filter when it holds a value the user chose:
// a non-zero picker tag, a typed number, a checked switch, a picked select
// option, or a selected client/location (those swap their input for a button).
// Date ranges always hold a value, so they never count.
function isCellActive(cell) {
  if (cell.querySelector('.filter-date')) return false;

  if (cell.querySelector('.client-name-field > .bp3-button')) return true;

  const tags = cell.querySelectorAll('.bp3-button .bp3-tag');
  for (const tag of tags) {
    // "All" selected is the same as no restriction.
    const text = tag.textContent.trim();
    if (text && text !== '0' && text.toLowerCase() !== 'all') return true;
  }

  const inputs = cell.querySelectorAll('input');
  for (const input of inputs) {
    if (input.type === 'checkbox' || input.type === 'radio') {
      if (input.checked) return true;
    } else if (!input.closest('.client-name-field') && input.value.trim()) {
      return true;
    }
  }

  const selects = cell.querySelectorAll('select');
  for (const select of selects) {
    if (select.selectedIndex > 0) return true;
  }

  return false;
}

class SearchFiltersToolbar extends Component {
  state = { open: false, activeCount: 0 };

  fieldsRef = React.createRef();

  componentDidMount() {
    const fields = this.fieldsRef.current;
    if (!fields) return;
    this.syncActive();
    // Pickers re-render their tags and autocompletes swap input/button, so
    // watch the DOM; typed text never mutates it, so listen for input too.
    this.observer = new MutationObserver(this.syncActive);
    this.observer.observe(fields, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    fields.addEventListener('input', this.syncActive);
    fields.addEventListener('change', this.syncActive);
  }

  componentDidUpdate() {
    this.syncActive();
  }

  componentWillUnmount() {
    const fields = this.fieldsRef.current;
    if (this.observer) this.observer.disconnect();
    if (fields) {
      fields.removeEventListener('input', this.syncActive);
      fields.removeEventListener('change', this.syncActive);
    }
  }

  syncActive = () => {
    const fields = this.fieldsRef.current;
    if (!fields) return;
    let activeCount = 0;
    for (const cell of fields.children) {
      const active = isCellActive(cell);
      cell.classList.toggle('is-active', active);
      if (active) activeCount += 1;
    }
    if (activeCount !== this.state.activeCount) {
      this.setState({ activeCount });
    }
  };

  toggle = () => {
    this.setState(({ open }) => ({ open: !open }));
  };

  renderCells() {
    return React.Children.map(this.props.children, (child) => {
      if (child === null || child === undefined || typeof child === 'boolean') {
        return null;
      }
      const childClassName = (child.props && child.props.className) || '';
      const wide = childClassName.includes('search-filters-toolbar__field-wide');
      return (
        <div className={wide ? 'filter-bar__cell filter-bar__cell--wide' : 'filter-bar__cell'}>
          {child}
        </div>
      );
    });
  }

  render() {
    const {
      className = '',
      header,
      actions,
      footer,
      navExtra,
    } = this.props;
    const { open, activeCount } = this.state;

    // On phones, running a search folds the panel so the results show.
    const searchButton =
      this.props.searchButton &&
      React.cloneElement(this.props.searchButton, {
        onClick: (event) => {
          this.setState({ open: false });
          const { onClick } = this.props.searchButton.props;
          if (onClick) onClick(event);
        },
      });

    const cells = this.renderCells() || [];
    const fieldCount = cells.filter(Boolean).length;
    const collapsible = fieldCount > COLLAPSE_AFTER;

    const rootClassName = [
      'filter-bar',
      collapsible ? 'filter-bar--collapsible' : 'filter-bar--compact',
      open && 'is-open',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <section className={rootClassName} aria-label="Filters">
        <div className="filter-bar__panel">
          {header && <div className="filter-bar__header">{header}</div>}
          {collapsible && (
            <div className="filter-bar__toggle-row">
              <button
                type="button"
                className="filter-bar__toggle"
                aria-expanded={open}
                onClick={this.toggle}
              >
                <Icon icon="filter-list" iconSize={14} />
                <span className="filter-bar__toggle-label">Filters</span>
                {activeCount > 0 && (
                  <span className="filter-bar__toggle-count">{activeCount}</span>
                )}
                <Icon
                  icon="chevron-down"
                  iconSize={14}
                  className="filter-bar__toggle-chevron"
                />
              </button>
              {searchButton && (
                <div className="filter-bar__toggle-search">{searchButton}</div>
              )}
            </div>
          )}

          <div className="filter-bar__fields" ref={this.fieldsRef}>
            {cells}
          </div>

          {(searchButton || actions) && (
            <div className="filter-bar__actions">
              {searchButton && (
                <div className="filter-bar__search">{searchButton}</div>
              )}
              {actions && <div className="filter-bar__secondary">{actions}</div>}
            </div>
          )}
        </div>
        {footer}
        {navExtra}
      </section>
    );
  }
}

SearchFiltersToolbar.propTypes = {
  className: PropTypes.string,
  header: PropTypes.node,
  searchButton: PropTypes.node,
  actions: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  navExtra: PropTypes.node,
};

export default SearchFiltersToolbar;
